/**
 * Capira Hero — assets/capira-hero.js
 * Scroll reveals, dashboard entrance, counter animation,
 * donut arc, demo modal, parallax tilt.
 */
(function () {
  'use strict';

  const pref = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const hero = document.getElementById('capira-hero');
  if (!hero) return;

  const demoBtn      = document.getElementById('capira-hero-demo-btn');
  const demoModal    = document.getElementById('capira-demo-modal');
  const demoClose    = document.getElementById('capira-demo-close');
  const demoBackdrop = document.getElementById('capira-demo-backdrop');

  /* 1. TEXT REVEAL */
  const revealEls = hero.querySelectorAll('[data-hero-reveal]');
  function revealText() { revealEls.forEach(el => el.classList.add('is-visible')); }
  if (pref) {
    revealText();
  } else {
    const textObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) { revealText(); textObserver.disconnect(); }
    }, { threshold: 0.1 });
    textObserver.observe(hero);
  }

  /* 2. DASHBOARD CARD ENTRANCE */
  const dashCards = hero.querySelectorAll('.capira-dash-card');
  function animateDash() {
    dashCards.forEach((card, i) => {
      setTimeout(() => card.classList.add('is-visible'), pref ? 0 : i * 100);
    });
  }
  if (pref) {
    animateDash();
  } else {
    const dashObserver = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) { setTimeout(animateDash, 300); dashObserver.disconnect(); }
    }, { threshold: 0.05 });
    dashObserver.observe(hero);
  }

  /* 3. COUNTER ANIMATION */
  const counterEl = hero.querySelector('[data-count-to]');
  function animateCounter(el) {
    const target = parseInt(el.dataset.countTo, 10);
    const dur = 1400;
    const start = performance.now();
    function tick(now) {
      const progress = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - progress, 4);
      el.textContent = '$' + Math.round(ease * target).toLocaleString('en-US');
      if (progress < 1) requestAnimationFrame(tick);
    }
    if (pref) { el.textContent = '$' + target.toLocaleString('en-US'); }
    else { requestAnimationFrame(tick); }
  }
  if (counterEl) {
    const networthCard = counterEl.closest('.capira-dash-card');
    if (networthCard) {
      const obs = new MutationObserver(mutations => {
        mutations.forEach(m => {
          if (m.target.classList.contains('is-visible')) {
            setTimeout(() => animateCounter(counterEl), pref ? 0 : 400);
            obs.disconnect();
          }
        });
      });
      obs.observe(networthCard, { attributes: true, attributeFilter: ['class'] });
    }
  }

  /* 4. DONUT ARC */
  const donutRing = hero.querySelector('.capira-donut__ring');
  if (donutRing) {
    const arcTarget = parseFloat(donutRing.dataset.arc || '141');
    const taxCard = donutRing.closest('.capira-dash-card');
    if (taxCard && !pref) {
      const arcObs = new MutationObserver(mutations => {
        mutations.forEach(m => {
          if (m.target.classList.contains('is-visible')) {
            donutRing.style.strokeDashoffset = 188.5 - arcTarget;
            arcObs.disconnect();
          }
        });
      });
      arcObs.observe(taxCard, { attributes: true, attributeFilter: ['class'] });
    } else if (pref) {
      donutRing.style.strokeDashoffset = 188.5 - arcTarget;
      donutRing.style.transition = 'none';
    }
  }

  /* 5. PARALLAX TILT */
  const dashGrid = hero.querySelector('.capira-hero__dash');
  const heroRight = hero.querySelector('.capira-hero__right');
  if (dashGrid && heroRight && !pref && window.matchMedia('(min-width: 1081px)').matches) {
    let rafId = null;
    heroRight.addEventListener('mousemove', e => {
      const rect = heroRight.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      if (!rafId) {
        rafId = requestAnimationFrame(() => {
          dashGrid.style.transform = `perspective(1200px) rotateY(${-4 + x * 6}deg) rotateX(${3 - y * 4}deg)`;
          rafId = null;
        });
      }
    });
    heroRight.addEventListener('mouseleave', () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      dashGrid.style.transition = 'transform 800ms cubic-bezier(0.16,1,0.3,1)';
      dashGrid.style.transform = 'perspective(1200px) rotateY(-4deg) rotateX(3deg)';
      setTimeout(() => { dashGrid.style.transition = ''; }, 820);
    });
  }

  /* 6. DEMO MODAL */
  if (demoBtn && demoModal) {
    function openModal() {
      demoModal.hidden = false;
      document.body.style.overflow = 'hidden';
      setTimeout(() => { const c = demoModal.querySelector('.capira-demo-modal__close'); if (c) c.focus(); }, 50);
    }
    function closeModal() {
      demoModal.hidden = true;
      document.body.style.overflow = '';
      if (demoBtn) demoBtn.focus();
    }
    demoBtn.addEventListener('click', openModal);
    if (demoClose) demoClose.addEventListener('click', closeModal);
    if (demoBackdrop) demoBackdrop.addEventListener('click', closeModal);
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !demoModal.hidden) closeModal(); });
  }

  /* 7. AI TEXT CYCLING */
  const aiTextEl = document.getElementById('capira-ai-text');
  const insights = [
    'You’re on track to save <strong>$3,200</strong> this quarter. Consider moving excess cash into your emergency fund — you’re 18% below your target.',
    'Your freelance income spiked <strong>+34%</strong> this month. Set aside <strong>$840</strong> now for estimated taxes to avoid surprises in Q4.',
    'You’ve saved <strong>$1,240</strong> more than last month. Your tax efficiency score is in the top 12% of creators on Capira.'
  ];
  let insightIdx = 0;
  if (aiTextEl && !pref) {
    setInterval(() => {
      aiTextEl.style.transition = 'opacity 300ms ease, transform 300ms ease';
      aiTextEl.style.opacity = '0';
      aiTextEl.style.transform = 'translateY(6px)';
      setTimeout(() => {
        insightIdx = (insightIdx + 1) % insights.length;
        aiTextEl.innerHTML = insights[insightIdx];
        aiTextEl.style.transition = 'opacity 500ms ease, transform 500ms cubic-bezier(0.16,1,0.3,1)';
        aiTextEl.style.opacity = '1';
        aiTextEl.style.transform = 'translateY(0)';
      }, 400);
    }, 5000);
  }

  /* 8. WORDMARK PARALLAX */
  const wordmark = hero.querySelector('.capira-hero__wordmark-wrap');
  if (wordmark && !pref) {
    let wTicking = false;
    window.addEventListener('scroll', () => {
      if (wTicking) return;
      wTicking = true;
      requestAnimationFrame(() => {
        wordmark.style.transform = `translateY(calc(-55% + ${window.scrollY * 0.25}px))`;
        wTicking = false;
      });
    }, { passive: true });
  }

  /* 9. SHOPIFY DESIGN MODE */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', e => {
      if (e.target.contains(hero)) { revealText(); animateDash(); }
    });
  }

})();
